/**
 * MultiSelectDropdown Component
 * Multi-select checkbox group inside a styled popover.
 * Matches Figma modal designs: olive checkmarks, dark bg, consistent spacing.
 * Used for: Access/visibility settings (who can see, comment, edit, join).
 */

import { ChevronsUpDown } from "lucide-react"
import { useCallback, useMemo, useState } from "react"
import { Checkbox } from "@/components/ui/checkbox"
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover"
import { cn } from "@/lib"

export type MultiSelectOption = string | { value: string; label: string }

interface MultiSelectDropdownProps {
  label?: string
  options: MultiSelectOption[]
  selectedOptions: string[]
  onChange: (options: string[]) => void
  disabledOptions?: string[]
  autoSelectedOptions?: string[]
  groupExclusion?: {
    group1: string[]
    group2: string[]
  }
  selectAllWith?: Record<string, string[]>
}

/** Normalize option to { value, label } */
function normalizeOption(opt: MultiSelectOption): { value: string; label: string } {
  return typeof opt === "string" ? { value: opt, label: opt } : opt
}

export function MultiSelectDropdown({
  label,
  options,
  selectedOptions,
  onChange,
  disabledOptions = [],
  autoSelectedOptions = [],
  groupExclusion,
  selectAllWith,
}: MultiSelectDropdownProps) {
  const [isOpen, setIsOpen] = useState(false)

  const normalized = options.map(normalizeOption)

  const isInGroup1 = useCallback(
    (value: string): boolean => {
      return groupExclusion?.group1.includes(value) ?? false
    },
    [groupExclusion],
  )

  const isInGroup2 = useCallback(
    (value: string): boolean => {
      return groupExclusion?.group2.includes(value) ?? false
    },
    [groupExclusion],
  )

  const toggleOption = useCallback(
    (value: string) => {
      if (disabledOptions.includes(value)) return
      if (autoSelectedOptions.includes(value)) return

      const isCurrentlySelected = selectedOptions.includes(value)

      if (isCurrentlySelected) {
        const newSelection = selectedOptions.filter(v => v !== value)
        if (newSelection.length === 0) return
        onChange(newSelection)
        return
      }

      let newSelection = [...selectedOptions, value]

      if (groupExclusion) {
        if (isInGroup1(value)) {
          newSelection = newSelection.filter(v => !isInGroup2(v))
        } else if (isInGroup2(value)) {
          newSelection = newSelection.filter(v => !isInGroup1(v))
        }
      }

      if (selectAllWith?.[value]) {
        const allValues = new Set([...newSelection, ...selectAllWith[value]])
        newSelection = Array.from(allValues)
      }

      onChange(newSelection)
    },
    [
      selectedOptions,
      autoSelectedOptions,
      disabledOptions,
      groupExclusion,
      selectAllWith,
      isInGroup1,
      isInGroup2,
      onChange,
    ],
  )

  const displayText = useMemo(() => {
    if (selectedOptions.length === 0) {
      return "Select options"
    }
    return selectedOptions.map(v => normalized.find(o => o.value === v)?.label ?? v).join(", ")
  }, [selectedOptions, normalized])

  return (
    <div className="flex flex-col md:flex-row md:items-center justify-between gap-0">
      {label && <span className="text-sm text-primary font-semibold flex-1">{label}</span>}

      <Popover open={isOpen} onOpenChange={setIsOpen}>
        <PopoverTrigger asChild>
          <button
            type="button"
            className="md:min-w-[200px] rounded-lg text-sm text-primary text-left focus:outline-none transition-all duration-200 flex items-center justify-end gap-1.25"
          >
            <span className="truncate">{displayText}</span>
            <ChevronsUpDown
              className={cn("transition-transform duration-200", isOpen && "rotate-180")}
              size={16}
              strokeWidth={2.5}
            />
          </button>
        </PopoverTrigger>
        <PopoverContent
          align="end"
          sideOffset={10}
          className="min-w-[280px] bg-popover border border-border rounded-[20px] p-[20px]"
        >
          <div className="flex flex-col gap-[10px]">
            {normalized.map(option => {
              const isChecked = selectedOptions.includes(option.value)
              const isDisabled = disabledOptions.includes(option.value)
              const isForcedSelected = autoSelectedOptions.includes(option.value)
              return (
                <label
                  key={option.value}
                  className={cn(
                    "flex items-center gap-[10px]",
                    !isDisabled && "cursor-pointer",
                    isDisabled && "cursor-not-allowed",
                  )}
                >
                  <Checkbox
                    checked={isChecked || isForcedSelected}
                    onCheckedChange={() => toggleOption(option.value)}
                    disabled={isDisabled}
                    className="size-[24px] rounded-[6px]"
                  />
                  <span
                    className={cn(
                      "text-[14px]",
                      isChecked || isForcedSelected ? "font-semibold" : "font-normal",
                      isDisabled && "opacity-60",
                    )}
                  >
                    {option.label}
                  </span>
                </label>
              )
            })}
          </div>
        </PopoverContent>
      </Popover>
    </div>
  )
}
